import { type CSSProperties } from 'react'
import { css, type CSSObject, type SerializedStyles } from '@emotion/react'
import { rem } from 'polished'

import { Z_INDEX_MEDIUM } from '@Styles/constants/layout'
import { RADIUS_1 } from '@Styles/constants/radius'
import { hasOne } from '@Utils/array'
import { getInnerBorderRadius } from '@Utils/border'

import { highContrast } from './a11y'

export const FOCUS_OFFSET = rem(2)
// le 'px' est voulu, on ne veut pas que le liseret scale avec le zoom. voir IVTS-3503
export const BORDER_SIZE = '2px'

const getPositionBorderFocus = (offSet: string): CSSProperties => ({
  inset: offSet,
})

/**
 * Fonction utilisée dans les overrides material car dans ce contexte on veut les règles CSS "pures"
 *
 * @param borderColor
 * @param borderRadius
 * @param offSet
 */
export const getRawFocusVisibleStyle = (
  borderColor: CSSProperties['color'],
  borderRadius = RADIUS_1,
  offSet: string = FOCUS_OFFSET,
): CSSObject => {
  const [
    borderTopLeftRadius = RADIUS_1,
    borderTopRightRadius = borderTopLeftRadius,
    borderBottomRightRadius = borderTopLeftRadius,
    borderBottomLeftRadius = borderTopLeftRadius,
  ] = borderRadius.split(' ')

  const focusOffset = (hasOne(offSet.split(' ')) && offSet) || FOCUS_OFFSET

  return {
    border: `${BORDER_SIZE} solid ${borderColor}`,
    borderRadius: `
      ${getInnerBorderRadius(borderTopLeftRadius, focusOffset)}
      ${getInnerBorderRadius(borderTopRightRadius, focusOffset)}
      ${getInnerBorderRadius(borderBottomRightRadius, focusOffset)}
      ${getInnerBorderRadius(borderBottomLeftRadius, focusOffset)}`,
    content: `''`,
    display: 'block',
    position: 'absolute',
    ...getPositionBorderFocus(offSet),
  }
}

/**
 * Fonction utilisée dans les composants où on passe par `@emotion`
 *
 * @param borderColor
 * @param borderRadius
 * @param offSet
 */
export const getFocusVisibleStyle = (
  borderColor: CSSProperties['color'],
  borderRadius: string = RADIUS_1,
  offSet: string = FOCUS_OFFSET,
): CSSObject => getRawFocusVisibleStyle(borderColor, borderRadius, offSet)

export const applyFocusVisible = (...args: Parameters<typeof getFocusVisibleStyle>): SerializedStyles =>
  css({
    '&::before': getFocusVisibleStyle(...args),
    ...highContrast({
      border: 'solid 1px transparent',
    }),
    position: 'relative',
  } as CSSObject)

export const getFocusVisibleIconButtonStyle = (borderColor: CSSProperties['color'], offSet: string): SerializedStyles =>
  css({
    border: `${BORDER_SIZE} solid ${borderColor}`,
    borderRadius: '50%',
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: Z_INDEX_MEDIUM,
    ...getPositionBorderFocus(offSet),
  })
